import {
  PAGE_HEADER_COLLAPSIBLE,
  PREVENT_DISMISS_ATTRIBUTE,
} from '@/hooks/useHeader/consts';
import useMeasure from '@/hooks/useMeasure/useMeasure';
import useScroll from '@/hooks/useScroll/useScroll';
import { animated, useSpring, useTrail } from '@react-spring/web';
import Clickable from '@uikit/components/Clickable/Clickable';
import { ClickableAction } from '@uikit/components/Clickable/consts';
import clsx from 'clsx';
import {
  CSSProperties,
  forwardRef,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import MagazineArticleHeaderProgressBar from '../MagazineArticleHeader/MagazineArticleHeaderProgressBar';
import MagazineArticleHeaderTitle from '../MagazineArticleHeader/MagazineArticleHeaderTitle';
import styles from './MagazinePageHeader.module.scss';
import { MagazinePageHeaderProps } from './types';

const MagazinePageHeader = forwardRef<HTMLElement, MagazinePageHeaderProps>(
  (props, ref) => {
    const {
      anchors = [],
      category,
      categorySlug,
      categoryTitle,
      isCollapsibleActive,
      onCollapsibleToggle,
      shouldHaveBackground = false,
      title,
    } = props;

    const [isTranslucent, setIsTranslucent] = useState(false);

    const pageHeaderRef = useRef<HTMLElement>(null);
    const linksHostRef = useRef<HTMLDivElement>(null);

    const { height: dropdownHeight } = useMeasure(linksHostRef, true);

    const dropdownLinksCount = anchors.length;
    const hasBackground = shouldHaveBackground || isCollapsibleActive;

    const dropdownAnimator = useSpring({
      delay: isCollapsibleActive ? 0 : 400,
      config: {
        mass: 1,
        tension: 200,
        friction: 16,
        clamp: !isCollapsibleActive,
      },
      height: isCollapsibleActive ? dropdownHeight : 0,
      opacity: isCollapsibleActive ? 1 : 0,
      pointerEvents: (isCollapsibleActive
        ? 'all'
        : 'none') as CSSProperties['pointerEvents'],
    });

    const dropdownLinksAnimator = useTrail(dropdownLinksCount, {
      config: {
        mass: 5,
        tension: 2000,
        friction: 100,
        clamp: !isCollapsibleActive,
      },
      delay: isCollapsibleActive ? 200 : 0,
      opacity: isCollapsibleActive ? 1 : 0,
      transform: isCollapsibleActive ? `translateX(0px)` : `translateX(-8px)`,
    });

    const toggleDropdown = () => onCollapsibleToggle(PAGE_HEADER_COLLAPSIBLE);

    useImperativeHandle<HTMLElement | null, HTMLElement | null>(
      ref,
      () => pageHeaderRef.current
    );

    useScroll(() => {
      if (isCollapsibleActive) {
        onCollapsibleToggle(PAGE_HEADER_COLLAPSIBLE);
      }

      if (pageHeaderRef.current) {
        const { top } = pageHeaderRef.current.getBoundingClientRect();

        /**
         * Due to overscrolling and the collapse behavior of Safari on mobile,
         * the only reliable way is to check if the body has been scrolled past
         * the top position of the PageHeader. However, on desktop browsers,
         * the offsetTop value is 0 once the PageHeader is sticky, that's why
         * the old calculation is needed as a fallback.
         */

        setIsTranslucent(
          pageHeaderRef.current.offsetTop > 0
            ? window.scrollY >= Math.floor(pageHeaderRef.current.offsetTop)
            : Math.floor(Math.abs(top)) === 0
        );
      }
    });

    return (
      <div
        className={clsx(styles.fullWidthContainer, {
          [styles.isInverted]: !isTranslucent && !hasBackground,
          [styles.isTranslucent]: isTranslucent && !hasBackground,
        })}
      >
        <nav
          className={clsx(styles.base, {
            [styles.hasBackdrop]: isCollapsibleActive,
            [styles.isInverted]: !isTranslucent && !hasBackground,
          })}
          ref={pageHeaderRef}
        >
          <div className={styles.dropdownActivator}>
            <MagazineArticleHeaderTitle
              activatorProps={{
                isActive: isCollapsibleActive,
                isEnabled: dropdownLinksCount > 0,
                onClick: toggleDropdown,
              }}
              category={category}
              categorySlug={categorySlug}
              categoryTitle={categoryTitle}
              hasBackground={shouldHaveBackground}
              hasTranslucentBackground={isTranslucent}
              title={title}
            />

            <MagazineArticleHeaderProgressBar isHidden={!isTranslucent} />
          </div>

          <div className={styles.linksHost}>
            <div
              className={clsx(styles.links, styles.isMeasured, {
                [styles.isCollapsed]: dropdownLinksCount > 0,
              })}
              ref={linksHostRef}
            >
              {anchors.map((anchor) => (
                <li className={styles.linkContainer} key={anchor.title}>
                  {anchor.title}
                </li>
              ))}
            </div>

            {anchors.length > 0 && (
              <animated.ul
                aria-hidden={dropdownLinksCount > 0 && !isCollapsibleActive}
                className={clsx(styles.links, {
                  [styles.isCollapsed]: dropdownLinksCount > 0,
                })}
                id="page-header-dropdown"
                style={dropdownLinksCount > 0 ? dropdownAnimator : undefined}
                {...{
                  [PREVENT_DISMISS_ATTRIBUTE]:
                    dropdownLinksCount > 0 && isCollapsibleActive,
                }}
                role="list"
              >
                {anchors.map((anchor, index) => {
                  const openingTrailIndex = index;
                  const closingTrailIndex =
                    dropdownLinksAnimator.length - 1 - index;

                  return (
                    <animated.li
                      className={styles.linkContainer}
                      style={
                        dropdownLinksCount > 0
                          ? dropdownLinksAnimator[
                              isCollapsibleActive
                                ? openingTrailIndex
                                : closingTrailIndex
                            ]
                          : undefined
                      }
                      key={anchor.title}
                    >
                      <Clickable
                        actionType={ClickableAction.Anchor}
                        anchor={anchor.anchor}
                        className={styles.link}
                        onClick={toggleDropdown}
                        tabIndex={isCollapsibleActive ? undefined : -1}
                      >
                        {anchor.title}
                      </Clickable>
                    </animated.li>
                  );
                })}
              </animated.ul>
            )}
          </div>
        </nav>
      </div>
    );
  }
);

MagazinePageHeader.displayName = 'MagazinePageHeader';

export default MagazinePageHeader;
